import { useState, useEffect, useRef, useMemo } from 'react'
import { Input } from '@/components/catalyst-ui/input'
import { Button } from '@/components/catalyst-ui/button'
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from '@/components/catalyst-ui/dropdown'
import { ChevronUpDownIcon } from '@heroicons/react/24/solid'
import { getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'

const COUNTRY_MAX_LENGTHS = {
  'US': 10, 'CA': 10, 'GB': 10, 'AU': 9, 'DE': 11, 'FR': 9, 'IT': 10, 'ES': 9,
  'NL': 9, 'BE': 9, 'CH': 9, 'AT': 10, 'SE': 9, 'NO': 8, 'DK': 8, 'FI': 10,
  'PL': 9, 'JP': 11, 'CN': 11, 'KR': 10, 'IN': 10, 'SG': 8, 'MY': 9, 'TH': 9,
  'PH': 10, 'ID': 10, 'VN': 10, 'BR': 11, 'MX': 10, 'AR': 10, 'CL': 9, 'CO': 10,
  'PE': 9, 'ZA': 9, 'NZ': 9, 'AE': 9, 'SA': 9, 'IL': 9, 'TR': 10, 'RU': 10, 'EG': 10,
}

const TIMEZONE_MAP = {
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Mexico_City': 'MX', 'America/Sao_Paulo': 'BR',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
  'Europe/London': 'GB', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT', 'Europe/Madrid': 'ES', 'Europe/Amsterdam': 'NL', 'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL',
  'Asia/Tokyo': 'JP', 'Asia/Shanghai': 'CN', 'Asia/Seoul': 'KR', 'Asia/Singapore': 'SG',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Kolkata': 'IN', 'Australia': 'AU',
  'Pacific/Auckland': 'NZ', 'Africa/Johannesburg': 'ZA',
}

const COMMON_COUNTRIES = ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'IN']

function getCountryFlag(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt())
  return String.fromCodePoint(...codePoints)
}

function getCountriesData() {
  return getCountries().map(code => ({
    code,
    name: en[code] || code,
    dialCode: `+${getCountryCallingCode(code)}`,
    flag: getCountryFlag(code),
  }))
}

function getCountryMaxLength(countryCode) {
  return COUNTRY_MAX_LENGTHS[countryCode] || 15
}

function getUserCountry(countries) {
  try {
    const locale = navigator.language || navigator.userLanguage || navigator.languages?.[0]
    if (locale) {
      const countryCode = locale.split('-')[1]?.toUpperCase() || locale.split('_')[1]?.toUpperCase()
      if (countryCode && countries.find(c => c.code === countryCode)) {
        return countryCode
      }
    }
    
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timezone) {
      for (const [tz, code] of Object.entries(TIMEZONE_MAP)) {
        if (timezone.includes(tz) && countries.find(c => c.code === code)) {
          return code
        }
      }
    }
  } catch (e) {
    console.error('[PhoneInput] Error detecting country:', e)
  }
  
  for (const code of COMMON_COUNTRIES) {
    if (countries.find(c => c.code === code)) {
      return code
    }
  }
  
  return countries[0]?.code || 'US'
}

function parsePhoneValue(value, COUNTRIES, defaultCountry) {
  if (!value || !value.trim()) {
    return { country: defaultCountry, phoneNumber: '' }
  }

  try {
    const parsed = parsePhoneNumber(value)
    if (parsed?.country) {
      const country = COUNTRIES.find(c => c.code === parsed.country)
      if (country) {
        return { country, phoneNumber: parsed.nationalNumber || '' }
      }
    }
  } catch (e) {
    console.error('[PhoneInput] Error parsing phone number:', e)
  }
  
  const country = COUNTRIES.find(c => value.startsWith(c.dialCode))
  if (country) {
    return { country, phoneNumber: value.replace(country.dialCode, '').trim() }
  }
  
  return { country: defaultCountry, phoneNumber: value.replace(/^\+/, '') }
}

export default function PhoneInput({ value = '', onChange, invalid = false, error, placeholder, className = '' }) {
  const COUNTRIES = useMemo(() => getCountriesData(), [])
  
  const defaultCountry = useMemo(() => {
    const userCountry = getUserCountry(COUNTRIES)
    return COUNTRIES.find(c => c.code === userCountry) || COUNTRIES[0]
  }, [COUNTRIES])
  
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const parsed = parsePhoneValue(value, COUNTRIES, defaultCountry)
    return parsed.country
  })
  
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const parsed = parsePhoneValue(value, COUNTRIES, defaultCountry)
    return parsed.phoneNumber
  })
  
  const inputRef = useRef(null)
  const lastSyncedValueRef = useRef(value)

  useEffect(() => {
    if (value !== lastSyncedValueRef.current) {
      lastSyncedValueRef.current = value
      const parsed = parsePhoneValue(value, COUNTRIES, defaultCountry)
      setSelectedCountry(parsed.country)
      setPhoneNumber(parsed.phoneNumber)
    }
  }, [value, COUNTRIES, defaultCountry])

  const maxLength = useMemo(() => getCountryMaxLength(selectedCountry.code), [selectedCountry.code])

  const handlePhoneChange = (e) => {
    let input = e.target.value.replace(/\D/g, '')
    if (input.length > maxLength) {
      input = input.slice(0, maxLength)
    }
    setPhoneNumber(input)
    if (onChange) {
      onChange({ target: { value: selectedCountry.dialCode + input } })
    }
  }

  const handleCountryChange = (country) => {
    setSelectedCountry(country)
    setPhoneNumber('')
    if (onChange) {
      onChange({ target: { value: country.dialCode } })
    }
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const validatePhone = () => {
    if (!phoneNumber || phoneNumber.length === 0) return null
    
    const fullNumber = selectedCountry.dialCode + phoneNumber
    try {
      const isValid = isValidPhoneNumber(fullNumber, selectedCountry.code)
      if (isValid) return true
      
      try {
        const parsed = parsePhoneNumber(fullNumber, selectedCountry.code)
        if (parsed?.isValid()) return true
      } catch (e) {
        console.error('[PhoneInput] Error validating phone number:', e)
      }
      
      return isValid
    } catch (e) {
      return phoneNumber.length >= 7
    }
  }

  const isValid = validatePhone()

  return (
    <div className={className}>
      <div className="flex items-center gap-2  mt-1.5">
        <Dropdown>
          <DropdownButton
            as={Button}
            plain
            className="flex items-center gap-1.5 px-3 !py-0.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 w-[90px] justify-between data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
          >
            <span className="flex items-center gap-1.5">
              <span className="text-lg">{selectedCountry.flag}</span>
              <span className="text-sm font-medium text-gray-700">{selectedCountry.dialCode}</span>
            </span>
            <ChevronUpDownIcon className="!w-4 !h-4 text-gray-500" />
          </DropdownButton>
          <DropdownMenu className="!max-h-[300px] !max-w-[250px] overflow-y-auto">
            {COUNTRIES.map((country) => (
              <DropdownItem
                key={country.code}
                onClick={() => handleCountryChange(country)}
                className="flex items-center gap-2"
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-sm">{country.name}</span>
                <span className="text-sm text-gray-500">{country.dialCode}</span>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>

        <Input
          ref={inputRef}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          invalid={invalid || (isValid === false) || !!error}
          placeholder={placeholder || `Enter phone number`}
          className="flex-1"
          maxLength={maxLength}
        />
      </div>
      {error && (
        <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{error}</p>
      )}
    </div>
  )
}
